import { useQueryClient } from 'react-query';
import { useRouter } from '@uirouter/react';

import { Button } from '@/core/components/Button';
import { Profile } from '@/core/hostmanagement/fdo/model';
import { Link } from '@/core/components/Link';
import { confirmAsync } from '@/core/services/modal.service/confirm';
import * as notifications from '@/core/services/notifications';
import {
  deleteProfile,
  duplicateProfile,
} from '@/core/hostmanagement/fdo/fdo.service';

interface Props {
  isFDOEnabled: boolean;
  selectedItems: Profile[];
}

export function FDOProfilesDatatableActions({
  isFDOEnabled,
  selectedItems,
}: Props) {
  const router = useRouter();
  const queryClient = useQueryClient();

  return (
    <div className="actionBar">
      <Link to="shbdeployer.endpoints.profile" className="space-left">
        <Button disabled={!isFDOEnabled}>
          <i className="fa fa-plus-circle space-right" aria-hidden="true" />
          Добавить профиль
        </Button>
      </Link>

      <Button
        disabled={!isFDOEnabled || selectedItems.length !== 1}
        onClick={() => onDuplicateProfileClick()}
      >
        <i className="fa fa-plus-circle space-right" aria-hidden="true" />
        Дублировать
      </Button>

      <Button
        disabled={!isFDOEnabled || selectedItems.length < 1}
        color="danger"
        onClick={() => onDeleteProfileClick()}
      >
        <i className="fa fa-trash-alt space-right" aria-hidden="true" />
        Удалить
      </Button>
    </div>
  );

  async function onDuplicateProfileClick() {
    const confirmed = await confirmAsync({
      title: 'Вы уверены?',
      message: 'Выбранный профиль будет скопирован. Продолжить?',
      buttons: {
        confirm: {
          label: 'Подтвердить',
          className: 'btn-primary',
        },
      },
    });

    if (!confirmed) {
      return;
    }

    try {
      const profile = selectedItems[0];
      const newProfile = await duplicateProfile(profile.id);
      notifications.success('Профиль успешно скопирован', profile.name);
      router.stateService.go('shbdeployer.endpoints.profile.edit', {
        id: newProfile.id,
      });
    } catch (err) {
      notifications.error(
        'Ошибка',
        err as Error,
        'Не удалось скопировать профиль'
      );
    }
  }

  async function onDeleteProfileClick() {
    const confirmed = await confirmAsync({
      title: 'Вы уверены?',
      message: 'Выбранные профили будут удалены. Продолжить?',
      buttons: {
        confirm: {
          label: 'Удалить',
          className: 'btn-danger',
        },
      },
    });

    if (!confirmed) {
      return;
    }

    await Promise.all(
      selectedItems.map(async (profile) => {
        try {
          await deleteProfile(profile.id);

          notifications.success('Профиль успешно удален', profile.name);
        } catch (err) {
          notifications.error(
            'Ошибка',
            err as Error,
            'Не удалось удалить профиль'
          );
        }
      })
    );

    await queryClient.invalidateQueries('fdo_profiles');
  }
}
